import { isString } from 'lodash';
import { useMemo } from 'react';

import { Passport } from '@dreryk/personal-external-identifier-common-api';

import { assertUnreachable, Bean, FieldControllerProps, Pesel, ValidationRules } from '@dreryk/react-common';
import { StyledMaxWidth, TextInput } from '@dreryk/react-components';

import { guardianTranslationKeys, medicalLeaveTranslationKeys } from '../../../../../../translations';

import {
   GuardianExternalIdentifierType,
   GuardianExternalIdentifierTypeVisitor,
} from './GuardianExternalIdentifierTypeField';

export type GuardianExternalIdentifierValue = string | null;

type GuardianExternalIdentifierValueFieldProps = FieldControllerProps<
   'guardianExternalIdentifierValue',
   GuardianExternalIdentifierValue
> & {
   readonly guardianExternalIdentifierTypeVisitor: GuardianExternalIdentifierTypeVisitor;
   readonly checkGuardianAlreadyAdded: (
      type: GuardianExternalIdentifierType,
      value: GuardianExternalIdentifierValue
   ) => boolean;
   readonly checkGuardianSameAsPatient: (
      type: GuardianExternalIdentifierType,
      value: GuardianExternalIdentifierValue
   ) => boolean;
};

export const GuardianExternalIdentifierValueField = (props: GuardianExternalIdentifierValueFieldProps): JSX.Element => {
   const { guardianExternalIdentifierTypeVisitor, checkGuardianAlreadyAdded, checkGuardianSameAsPatient } = props;

   const validationRules = useMemo(() => {
      return guardianExternalIdentifierTypeVisitor<ValidationRules<Bean>>({
         visitPesel: () => ({
            required: true,
            maxLength: Pesel.PESEL_LENGTH,
            validate: (value) => {
               if (!isString(value)) {
                  return assertUnreachable(undefined);
               }

               if (!Pesel.isValid(value)) {
                  return medicalLeaveTranslationKeys.personalData.identifierValue.validate.invalidPesel;
               }

               if (checkGuardianSameAsPatient('Pesel', value)) {
                  return guardianTranslationKeys.guardianSameAsPatient;
               }

               if (checkGuardianAlreadyAdded('Pesel', value)) {
                  return guardianTranslationKeys.guardianAlreadyAdded;
               }

               return undefined;
            },
         }),
         visitPassport: () => ({
            required: true,
            maxLength: Passport.MAX_LENGTH,
            pattern: {
               value: new RegExp(Passport.PATTERN, 'u'),
               message: medicalLeaveTranslationKeys.personalData.identifierValue.validate.invalidPassportPattern,
            },
            validate: (value) => {
               if (!isString(value)) {
                  return assertUnreachable(undefined);
               }

               if (!Passport.isValueValid(value)) {
                  return medicalLeaveTranslationKeys.personalData.identifierValue.validate.invalidPassport;
               }

               if (checkGuardianSameAsPatient('Passport', value)) {
                  return guardianTranslationKeys.guardianSameAsPatient;
               }

               if (checkGuardianAlreadyAdded('Passport', value)) {
                  return guardianTranslationKeys.guardianAlreadyAdded;
               }

               return undefined;
            },
         }),
      });
   }, [guardianExternalIdentifierTypeVisitor, checkGuardianAlreadyAdded, checkGuardianSameAsPatient]);

   return (
      <StyledMaxWidth maxWidth={263}>
         <TextInput
            testId={'guardianExternalIdentifierValue'}
            label={medicalLeaveTranslationKeys.personalData.identifierValue.label}
            placeholder={guardianExternalIdentifierTypeVisitor(
               (type) => medicalLeaveTranslationKeys.personalData.identifierValue.placeholder[type]
            )}
            controller={props.register('guardianExternalIdentifierValue', validationRules)}
            defaultValue={null}
         />
      </StyledMaxWidth>
   );
};
